import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Advertiser", "Publisher", "Other"];

const EMAILS = [
  { label: "For Advertisers", email: "sales@adstellarmedia.com" },
  { label: "For Publishers", email: "supply@adstellarmedia.com" },
  { label: "Other Enquiries", email: "biz@adstellarmedia.com" },
];

const EMPTY = { name: "", email: "", phone: "", company: "", type: "Advertiser", message: "" };

const inputCls =
  "w-full bg-transparent border border-white/15 px-5 py-4 text-sm text-[#F8F8F8] placeholder:text-[#52525B] focus:border-[#8B5CF6] focus:outline-none transition-colors duration-300";

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Enquiry sent — our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please email us at biz@adstellarmedia.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-28 md:py-40 border-t border-white/10 bg-[#121214]">
      <Toaster position="top-center" richColors />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <Overline>Start a Conversation</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="enquiry-heading">
              Tell Us Where<br />You Want to <span className="italic text-[#8B5CF6]">Grow.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base text-[#A0A0AB] font-light leading-relaxed">
              Share a few details and our team will come back with a plan —
              the right inventory, the right partners, and the right CPMs for
              your goals.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-12 space-y-6">
              {EMAILS.map((c) => (
                <li key={c.email} className="border-l border-white/10 pl-6">
                  <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#52525B]">{c.label}</p>
                  <a
                    href={`mailto:${c.email}`}
                    data-testid={`enquiry-quick-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="mt-1 inline-block text-base text-[#F8F8F8] hover:text-[#8B5CF6] transition-colors duration-300"
                  >
                    {c.email}
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="md:col-span-7">
          <form onSubmit={submit} data-testid="enquiry-form" className="border border-white/10 bg-[#09090A] p-8 md:p-12">
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your Name *"
                data-testid="enquiry-name-input"
                className={inputCls}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Work Email *"
                data-testid="enquiry-email-input"
                className={inputCls}
              />
              <input
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone"
                data-testid="enquiry-phone-input"
                className={inputCls}
              />
              <input
                value={form.company}
                onChange={set("company")}
                placeholder="Company / Brand"
                data-testid="enquiry-company-input"
                className={inputCls}
              />
            </div>

            <div className="mt-8">
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#52525B] mb-4">I am an</p>
              <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Enquiry type">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, type: t })}
                    data-testid={`enquiry-type-${t.toLowerCase()}`}
                    className={`px-6 py-3 font-accent text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
                      form.type === t
                        ? "bg-[#8B5CF6] border-[#8B5CF6] text-white"
                        : "border-white/15 text-[#A0A0AB] hover:border-[#8B5CF6] hover:text-[#F8F8F8]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              required
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your goals — budgets, markets, KPIs... *"
              rows={5}
              data-testid="enquiry-message-input"
              className={`${inputCls} mt-8 resize-none`}
            />

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiry-submit-button"
                className="group inline-flex items-center gap-3 bg-[#8B5CF6] text-white px-8 py-5 font-accent text-xs tracking-[0.25em] uppercase hover:bg-[#A78BFA] disabled:opacity-60 transition-colors duration-300"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                )}
              </button>
              {sent && (
                <p data-testid="enquiry-success-message" className="text-sm text-[#8B5CF6]">
                  Received — we'll get back to you within 24 hours.
                </p>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
